import type { ReactNode } from "react";
import "./ExplorerWizard.css";

export type ExplorerWizardShellProps = {
  progressLabel: string;
  step?: number;
  totalSteps?: number;
  stepNumbers?: boolean;
  children: ReactNode;
  error?: string | null;
  onBack?: () => void;
  showBack?: boolean;
  onNext?: () => void;
  nextFormId?: string;
  nextLabel?: string;
  nextDisabled?: boolean;
  loading?: boolean;
  showFooter?: boolean;
  /** "below" = Back/Next under the white card (checkout reference). */
  footerPlacement?: "inside" | "below";
  /** Payment/membership step — centered card, mockup #2 header & buttons. */
  checkoutDesign?: boolean;
  /** Show Connect logo in the progress header (left-aligned). */
  showHeaderLogo?: boolean;
};

export default function ExplorerWizardShell({
  progressLabel: _progressLabel,
  step: _step,
  totalSteps: _totalSteps,
  stepNumbers: _stepNumbers = true,
  children,
  error,
  onBack,
  showBack = true,
  onNext,
  nextFormId,
  nextLabel = "Next",
  nextDisabled = false,
  loading = false,
  showFooter = true,
  footerPlacement = "inside",
  checkoutDesign = false,
  showHeaderLogo = true,
}: ExplorerWizardShellProps) {
  const footerContent = showFooter ? (
    <>
      {showBack && onBack ? (
        <button
          type="button"
          className="btn explorer-wizard-btn explorer-wizard-btn--ghost"
          onClick={onBack}
          disabled={loading}
        >
          Back
        </button>
      ) : null}
      {onNext || nextFormId ? (
        <button
          type={nextFormId ? "submit" : "button"}
          form={nextFormId}
          className="btn explorer-wizard-btn explorer-wizard-btn--primary"
          onClick={nextFormId ? undefined : onNext}
          disabled={loading || nextDisabled}
        >
          {loading ? "Please wait…" : nextLabel}
        </button>
      ) : null}
    </>
  ) : null;

  const rootClass = [
    "explorer-wizard-root",
    "position-relative",
    checkoutDesign ? "explorer-wizard-root--checkout" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const stackClass = ["explorer-wizard-stack", "mx-auto", checkoutDesign ? "explorer-wizard-stack--checkout" : ""]
    .filter(Boolean)
    .join(" ");

  const cardClass = ["explorer-wizard-card", checkoutDesign ? "explorer-wizard-card--checkout" : ""]
    .filter(Boolean)
    .join(" ");

  const progressClass = ["explorer-wizard-progress", checkoutDesign ? "explorer-wizard-progress--checkout" : ""]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={rootClass}>
      <div className="explorer-wizard-backdrop" aria-hidden />
      <div className={stackClass}>
        <div className={cardClass}>
          {showHeaderLogo ? (
            <div className={progressClass}>
              <img
                src="/connect-logo.png"
                alt="SMBF logo"
                className="explorer-wizard-progress__logo"
              />
            </div>
          ) : null}
          <div className="explorer-wizard-body">
            {error ? (
              <div className="alert alert-danger py-2 small mb-3" role="alert">
                {error}
              </div>
            ) : null}
            {children}
          </div>
          {showFooter && footerPlacement === "inside" ? (
            <>
              <div className="explorer-wizard-divider" />
              <div
                className={[
                  "explorer-wizard-footer",
                  checkoutDesign ? "explorer-wizard-footer--checkout" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {footerContent}
              </div>
            </>
          ) : null}
        </div>
        {showFooter && footerPlacement === "below" ? (
          <div className="explorer-wizard-footer-below">{footerContent}</div>
        ) : null}
      </div>
    </div>
  );
}
